/* Trio Sporting — Holiday Camps enrolment journey (Marcello giro-3 review, 2026-08-02).
   Vanilla, dependency-free. Reads config from #camp-enroll-config (rendered by
   sections/m-camp-enroll.liquid), camp dates from assets/calendar.json (same source the
   on-page date list at sections/m-camp-dates.liquid uses, so the two never disagree).

   Journey: pick a date -> how many riders (siblings/friends = minimum 2, same SAR 1,000/rider
   rate) -> each rider's name -> review -> real Shopify cart via Ajax Cart API, one line item
   per rider (POST /cart/add.js, credentials included cross-origin — the same pattern
   trio-booking-widget already relies on against this store) -> redirect to checkout.

   Early Bird: computed automatically from today's date vs. each camp's real start date
   (>= earlyBirdMinDays away = eligible). A camp entered via the "Early Bird" card only ever
   lists eligible dates; entering via "Standard" on an eligible date offers a one-click switch;
   2+ riders always price at the Sibling/Friends rate regardless of entry point (that rate
   already equals the Early Bird rate, so there's no conflict to resolve there). */
(function () {
  'use strict';

  var MODAL_ID = 'camp-enroll';
  var cfg = null;
  var state = null;

  function readConfig() {
    var node = document.getElementById('camp-enroll-config');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  function freshState(tier) {
    return {
      tier: tier,                 // entry tier: 'standard' | 'sibling' | 'early'
      step: 1,                    // 1 date, 2 riders, 3 review
      camps: [],                  // future camps, loaded once
      loaded: false,
      loadError: false,
      camp: null,                 // chosen camp object
      riderCount: tier === 'sibling' ? 2 : 1,
      riders: [{ first: '', last: '' }],
      useEarlyBird: tier === 'early',
      submitting: false,
      submitError: false
    };
  }

  function daysFromToday(iso) {
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var d = new Date(iso + 'T00:00:00');
    return Math.round((d - today) / 86400000);
  }

  function fmtSar(n) { return 'SAR ' + n.toLocaleString('en-US'); }

  function loadCalendar() {
    if (!cfg) return Promise.resolve([]);
    return fetch(cfg.calendarUrl, { cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('calendar fetch failed'); return r.json(); })
      .then(function (data) {
        var camps = (data && data.camps) || [];
        return camps
          .filter(function (c) { return c.date_start && daysFromToday(c.date_start) >= 0; })
          .sort(function (a, b) { return a.date_start < b.date_start ? -1 : 1; });
      })
      .catch(function () { return []; });
  }

  // Effective pricing tier for the CURRENT state (riders count can override the entry tier).
  function effectiveTier() {
    if (state.riderCount >= 2) return 'sibling';
    if (state.tier === 'early' || state.useEarlyBird) return 'early';
    return 'standard';
  }

  function isEligible(camp) {
    return cfg && camp && daysFromToday(camp.date_start) >= cfg.earlyBirdMinDays;
  }

  // ---------- rendering ----------

  function render() {
    var body = document.querySelector('[data-camp-body]');
    if (!body) return;
    document.querySelectorAll('[data-step-dot]').forEach(function (li) {
      var n = Number(li.getAttribute('data-step-dot'));
      li.classList.toggle('is-active', n === state.step);
      li.classList.toggle('is-done', n < state.step);
    });

    if (!state.loaded) {
      body.innerHTML = '<p class="m-camp-loading">Loading camp dates…</p>';
      return;
    }
    if (state.step === 1) return renderStepDate(body);
    if (state.step === 2) return renderStepRiders(body);
    return renderStepReview(body);
  }

  function renderStepDate(body) {
    var pool = state.tier === 'early' ? state.camps.filter(isEligible) : state.camps;
    var html = '';
    html += '<p class="m-camp-step__title">Choose your camp date</p>';
    html += '<p class="m-camp-step__sub">' + (state.tier === 'early'
      ? 'Early Bird pricing (SAR ' + cfg.tiers.early.price + ') only applies to a camp starting ' + cfg.earlyBirdMinDays + '+ days from today — these are the camps that currently qualify.'
      : 'Pick any upcoming camp. Camps starting ' + cfg.earlyBirdMinDays + '+ days out also qualify for Early Bird pricing.') + '</p>';

    if (!pool.length) {
      html += '<div class="m-camp-empty">' + (state.tier === 'early'
        ? 'No camp currently starts ' + cfg.earlyBirdMinDays + '+ days from today, so Early Bird isn’t applicable right now — not applicable for any date on the calendar at the moment.'
        : 'No upcoming camp dates are published yet.') + ' <a href="' + cfg.fallbackContactUrl + '">Ask us to notify you →</a></div>';
    } else {
      html += '<div class="m-camp-dates-list">';
      pool.forEach(function (c, i) {
        var eligible = isEligible(c);
        var sel = state.camp && state.camp.date_start === c.date_start;
        html += '<div class="m-camp-date-opt' + (sel ? ' is-selected' : '') + '" data-camp-pick="' + i + '" role="button" tabindex="0">';
        html += '<div><b>' + c.name + '</b><span>' + c.detail + '</span></div>';
        html += eligible ? '<span class="m-camp-badge m-camp-badge--gold">Early Bird eligible</span>' : '';
        html += '</div>';
      });
      html += '</div>';
    }
    html += '<div class="m-camp-nav"><span></span><button type="button" class="m-btn m-btn--dark" data-camp-next' + (state.camp ? '' : ' disabled') + '>Continue →</button></div>';
    body.innerHTML = html;

    body.querySelectorAll('[data-camp-pick]').forEach(function (el, idx) {
      el.addEventListener('click', function () {
        state.camp = pool[Number(el.getAttribute('data-camp-pick'))];
        render();
      });
    });
    var next = body.querySelector('[data-camp-next]');
    if (next) next.addEventListener('click', function () { if (state.camp) { state.step = 2; render(); } });
  }

  function renderStepRiders(body) {
    var min = state.tier === 'sibling' ? 2 : 1;
    while (state.riders.length < state.riderCount) state.riders.push({ first: '', last: '' });
    state.riders.length = state.riderCount;

    var html = '';
    html += '<p class="m-camp-step__title">' + state.camp.name + '</p>';
    html += '<p class="m-camp-step__sub">' + state.camp.detail + '</p>';

    html += '<div class="m-camp-riders-head"><div><b>Do you have siblings or friends joining too?</b></div>';
    html += '<div class="m-camp-stepper"><button type="button" data-camp-dec' + (state.riderCount <= min ? ' disabled' : '') + '>−</button><output>' + state.riderCount + '</output><button type="button" data-camp-inc>+</button></div></div>';
    html += '<p class="m-camp-sibling-note">' + (state.riderCount >= 2
      ? state.riderCount + ' riders on this booking — Sibling/Friends rate applies to all of them: ' + fmtSar(cfg.tiers.sibling.price) + ' per rider (minimum 2).'
      : 'One rider on this booking. Add a unit above for a sibling or friend riding the same camp — minimum 2 riders on the Sibling/Friends rate.') + '</p>';

    if (state.riderCount === 1 && state.tier !== 'early' && isEligible(state.camp)) {
      html += '<div class="m-camp-early-nudge"><span>This date qualifies for <b>Early Bird</b> — ' + fmtSar(cfg.tiers.early.price) + ' instead of ' + fmtSar(cfg.tiers.standard.price) + '.</span>';
      html += '<button type="button" class="m-btn m-btn--gold" data-camp-earlybird-toggle style="padding:.5rem 1rem;">' + (state.useEarlyBird ? 'Applied ✓' : 'Switch & save') + '</button></div>';
    }

    state.riders.forEach(function (r, i) {
      html += '<div class="m-camp-rider"><b>Rider ' + (i + 1) + '</b><div class="m-two">';
      html += '<div class="m-field"><label>First name</label><input type="text" data-camp-rider-first="' + i + '" value="' + (r.first || '').replace(/"/g, '&quot;') + '" required></div>';
      html += '<div class="m-field"><label>Last name</label><input type="text" data-camp-rider-last="' + i + '" value="' + (r.last || '').replace(/"/g, '&quot;') + '" required></div>';
      html += '</div></div>';
    });

    var allNamed = state.riders.every(function (r) { return r.first.trim() && r.last.trim(); });
    html += '<div class="m-camp-nav"><button type="button" class="m-btn m-btn--ghost-dark" data-camp-back>← Back</button><button type="button" class="m-btn m-btn--dark" data-camp-next' + (allNamed ? '' : ' disabled') + '>Review →</button></div>';
    body.innerHTML = html;

    var dec = body.querySelector('[data-camp-dec]'), inc = body.querySelector('[data-camp-inc]');
    if (dec) dec.addEventListener('click', function () { if (state.riderCount > min) { state.riderCount--; render(); } });
    if (inc) inc.addEventListener('click', function () { state.riderCount++; render(); });
    var eb = body.querySelector('[data-camp-earlybird-toggle]');
    if (eb) eb.addEventListener('click', function () { state.useEarlyBird = !state.useEarlyBird; render(); });
    body.querySelectorAll('[data-camp-rider-first]').forEach(function (inp) {
      inp.addEventListener('input', function () { state.riders[Number(inp.getAttribute('data-camp-rider-first'))].first = inp.value; syncNextEnabled(body); });
    });
    body.querySelectorAll('[data-camp-rider-last]').forEach(function (inp) {
      inp.addEventListener('input', function () { state.riders[Number(inp.getAttribute('data-camp-rider-last'))].last = inp.value; syncNextEnabled(body); });
    });
    var back = body.querySelector('[data-camp-back]');
    if (back) back.addEventListener('click', function () { state.step = 1; render(); });
    var next = body.querySelector('[data-camp-next]');
    if (next) next.addEventListener('click', function () {
      if (state.riders.every(function (r) { return r.first.trim() && r.last.trim(); })) { state.step = 3; render(); }
    });
  }

  function syncNextEnabled(body) {
    var allNamed = state.riders.every(function (r) { return r.first.trim() && r.last.trim(); });
    var next = body.querySelector('[data-camp-next]');
    if (next) next.disabled = !allNamed;
  }

  function renderStepReview(body) {
    var tier = effectiveTier();
    var price = cfg.tiers[tier].price;
    var total = price * state.riderCount;

    var html = '';
    html += '<p class="m-camp-step__title">Review your enrolment</p>';
    html += '<div class="m-camp-review-row"><span>' + state.camp.name + '</span><span>' + state.camp.detail + '</span></div>';
    state.riders.forEach(function (r) {
      html += '<div class="m-camp-review-row"><span>' + r.first + ' ' + r.last + ' — ' + cfg.tiers[tier].label + '</span><span>' + fmtSar(price) + '</span></div>';
    });
    html += '<div class="m-camp-review-total"><span>Total</span><span>' + fmtSar(total) + '</span></div>';
    if (state.submitError) {
      html += '<p class="m-camp-error">We couldn’t reach the live checkout from this preview. <a href="' + cfg.fallbackContactUrl + '">Finish your enrolment with our team instead →</a></p>';
    }
    html += '<div class="m-camp-nav"><button type="button" class="m-btn m-btn--ghost-dark" data-camp-back>← Back</button><button type="button" class="m-btn m-btn--gold" data-camp-checkout' + (state.submitting ? ' disabled' : '') + '>' + (state.submitting ? 'Processing…' : 'Proceed to Checkout →') + '</button></div>';
    body.innerHTML = html;

    var back = body.querySelector('[data-camp-back]');
    if (back) back.addEventListener('click', function () { state.step = 2; render(); });
    var go = body.querySelector('[data-camp-checkout]');
    if (go) go.addEventListener('click', checkout);
  }

  function checkout() {
    var tier = effectiveTier();
    var variantId = cfg.tiers[tier].variantId;
    if (!variantId) { state.submitError = true; render(); return; }

    state.submitting = true; state.submitError = false; render();

    var items = state.riders.map(function (r) {
      return {
        id: variantId,
        quantity: 1,
        properties: {
          'Camp': state.camp.name,
          'Dates': state.camp.detail,
          'Rider Name': (r.first + ' ' + r.last).trim(),
          'Rate': cfg.tiers[tier].label
        }
      };
    });

    fetch(cfg.checkoutDomain + '/cart/add.js', {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart add failed');
      window.location.href = cfg.checkoutDomain + '/checkout';
    }).catch(function () {
      state.submitting = false; state.submitError = true; render();
    });
  }

  // ---------- open / close ----------

  function openModal(tier) {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    state = freshState(tier);
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    render();
    loadCalendar().then(function (camps) {
      state.camps = camps;
      state.loaded = true;
      render();
    });
  }

  function closeModal() {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.addEventListener('DOMContentLoaded', function () {
    cfg = readConfig();
    if (!cfg) return;
  });

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-open-camp-enroll]');
    if (opener) {
      e.preventDefault();
      if (!cfg) cfg = readConfig();
      openModal(opener.getAttribute('data-open-camp-enroll') || 'standard');
      return;
    }
    if (e.target.closest('[data-camp-close]')) closeModal();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeModal();
  });
})();
